import { BodyImageManualStatus } from 'models/bodyImage.types';
import { PatientsQueryParams, SymptomsResponseProps } from 'models/patients.types';
import { apiSlice } from 'store/api/apiSlice';

import { HistoryProps } from '../history/history.types';
import { transformPatientsResponse } from './patients.settings';
import {
  BillingHistoryResponseProps,
  CallTokenProps,
  CancelProgramRequestProps,
  CancelProgramResponse,
  ChartHealthMetrics,
  CheckInsuranceResponseProps,
  DefaultQueryParams,
  DeletePatientResponseProps,
  ExtendBillingRequestProps,
  GetChartHealthMetricsProps,
  PatientIdsResponse,
  PatientProps,
  PatientsBulkUpdateParams,
  PatientsBulkUpdateResponse,
  PatientsResponse,
  ProgramsDetailsResponse,
  ProgramsProps,
  ReactivatePatientResponseProps,
  ReportProps,
  UpdateNotesRequestProps,
  UpdateNotesResponseProps
} from './patients.types';
import { patientTagsApi } from './patientTags/patientTagsApi';

export const patientsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    cancelProgram: builder.mutation<CancelProgramResponse, CancelProgramRequestProps>({
      async onQueryStarted(props, { dispatch, queryFulfilled }) {
        await queryFulfilled;
        setTimeout(() => {
          dispatch(
            patientTagsApi.util.invalidateTags([
              { id: props.patientId, type: 'Patient' },
              { id: 'LIST', type: 'Tags' }
            ])
          );
        }, 10000);
      },
      query: ({ cancelReasonCode, note, patientId, programId, refundAmount }) => ({
        body: { cancelReasonCode, note, programId, refundAmount },
        method: 'PATCH',
        url: `/patients/${patientId}/cancel-program`
      })
    }),
    changePatientStatus: builder.mutation<
      { message: string },
      { patientId: string; status: string }
    >({
      invalidatesTags: (_result, _error, arg) => [{ id: arg.patientId, type: 'Patient' }],
      query: ({ patientId, status }) => ({
        body: { status },
        method: 'PATCH',
        url: `/patients/${patientId}/change-status`
      })
    }),
    createPlanRecommend: builder.mutation<
      { message: string },
      { body: { planRecommendationId: string }; patientId: string }
    >({
      invalidatesTags: (_result, _error, arg) => [{ id: arg.patientId, type: 'Patient' }],
      query: ({ body, patientId }) => ({
        body,
        method: 'POST',
        url: `/patients/${patientId}/plan-recommendation`
      })
    }),
    deactivatePatient: builder.mutation<
      DeletePatientResponseProps,
      { chargifyCancelMessage?: string; patientId: string; subscriptionCancelReasonCode: string }
    >({
      invalidatesTags: (_result, _error, arg) => [{ id: arg.patientId, type: 'Patient' }],
      query: ({ chargifyCancelMessage, patientId, subscriptionCancelReasonCode }) => ({
        body: {
          subscriptionCancelReasonCode,
          ...(chargifyCancelMessage && { chargifyCancelMessage })
        },
        method: 'DELETE',
        url: `/patients/${patientId}`
      })
    }),
    deleteClearAtRiskStatus: builder.mutation({
      invalidatesTags: (_result, _error, arg) => [{ id: arg.patientId, type: 'Patient' }],
      query: ({ patientId }) => ({
        method: 'DELETE',
        url: `/patients/${patientId}/asq-alert`
      }),
      transformResponse: (response: { message: string }) => response
    }),
    disablePlanRecommendation: builder.mutation<{ message: string }, { patientId: string }>({
      invalidatesTags: (_result, _error, arg) => [{ id: arg.patientId, type: 'Patient' }],
      query: ({ patientId }) => ({
        method: 'PATCH',
        url: `/patients/${patientId}/disable-plan-recommendation`
      })
    }),
    extendBilling: builder.mutation<{ message: string }, ExtendBillingRequestProps>({
      invalidatesTags: (_result, _error, arg) => [
        { id: arg.patientId, type: 'Patient' },
        'Subscription'
      ],
      query: ({ currentProgramId, days, patientId }) => ({
        body: { currentProgramId, days },
        method: 'PATCH',
        url: `/patients/${patientId}/extend-billing-date`
      })
    }),
    getAllergies: builder.query<string[], { patientId: string }>({
      query: ({ patientId }) => `/patients/${patientId}/allergies`,
      transformResponse: (response: { data: string[] }) => response.data
    }),
    getBenefits: builder.query<CheckInsuranceResponseProps, { patientId: string }>({
      providesTags: ['Benefits'],
      query: ({ patientId }) => `/patients/${patientId}/check-benefits`,
      transformResponse: (response: { data: CheckInsuranceResponseProps }) => response.data
    }),
    getBillingHistory: builder.query<
      BillingHistoryResponseProps,
      { id: string; params: DefaultQueryParams }
    >({
      providesTags: ['Billing'],
      query: ({ id, params }) => {
        return {
          params: {
            ...params,
            sortOrder: params.sortOrder ? params.sortOrder.toLowerCase() : undefined
          },
          url: `/patients/${id}/billing-history`
        };
      }
    }),
    getCallToken: builder.query<CallTokenProps, { id: string }>({
      query: ({ id }) => `patients/voice-calls/${id}/token`,
      transformResponse: (response: { data: CallTokenProps }) => response.data
    }),

    getChartHealthMetrics: builder.query<
      { data: ChartHealthMetrics; message: string },
      { params: GetChartHealthMetricsProps; patientId: string }
    >({
      providesTags: ['ChartHealthMetrics'],
      query: ({ params, patientId }) => ({
        params,
        url: `/patients/${patientId}/chart-health-metrics`
      })
    }),
    getHistory: builder.query<HistoryProps, { id: string; params: string }>({
      query: ({ id, params }) => `/patients/${id}/action-logs${params}`
    }),
    getPatient: builder.query<PatientProps, { id: string }>({
      providesTags: (_result, _error, arg) => [{ id: arg.id, type: 'Patient' }],
      query: ({ id }) => `/patients/${id}`,
      transformResponse: (response: { data: PatientProps }) => response.data
    }),
    getPatientChart: builder.query<{ [key: string]: { [key: string]: string }[] }, { id: string }>({
      query: ({ id }) => `/patients/${id}/my-chart`,
      transformResponse: (response: { data: { [key: string]: { [key: string]: string }[] } }) =>
        response.data
    }),
    getPatientElationId: builder.query<string, { patientId: string }>({
      providesTags: (_result, _error, arg) => [{ id: arg.patientId, type: 'Patient' }],
      query: ({ patientId }) => `/patients/${patientId}/elationId`,
      transformResponse: (response: { data: { elationId: string } }) => response.data.elationId
    }),
    // Materializes patient ids matching the current filters, capped by `limit`.
    // Powers "select all matching" and "select first N" without paging records.
    getPatientIds: builder.query<PatientIdsResponse, { params: PatientsQueryParams }>({
      query: ({ params }) => ({
        params,
        url: '/patients/ids'
      }),
      transformResponse: (response: { data: { ids: string[] }; info: { totalCount: number } }) => ({
        ids: response.data.ids,
        totalCount: response.info.totalCount
      })
    }),
    getPatientPrograms: builder.query<ProgramsProps[], { patientId: string }>({
      providesTags: (_result, _error, arg) => [{ id: arg.patientId, type: 'Patient' }],
      query: ({ patientId }) => `/patients/${patientId}/programs`,
      transformResponse: (response: { data: ProgramsProps[] }) => response.data
    }),
    getPatients: builder.query<
      { patients: PatientsResponse[]; totalCount: number },
      { params: PatientsQueryParams }
    >({
      providesTags: (result) =>
        result
          ? [
              'Patient',
              ...result.patients.map(({ _id }) => ({ id: _id, type: 'Patient' as const }))
            ]
          : ['Patient'],
      query: ({ params }) => ({
        params,
        url: '/patients'
      }),
      transformResponse: (response: {
        data: PatientsResponse[];
        info: { totalCount: number };
      }) => ({
        patients: transformPatientsResponse(response.data),
        totalCount: response.info.totalCount
      })
    }),
    getProblems: builder.query<string[], { patientId: string }>({
      query: ({ patientId }) => `/patients/${patientId}/problems`,
      transformResponse: (response: { data: string[] }) => response.data
    }),
    getProgramsDetails: builder.query<ProgramsDetailsResponse, { patientId: string }>({
      providesTags: (_result, _error, arg) => [{ id: arg.patientId, type: 'Patient' }],
      query: ({ patientId }) => `/patients/${patientId}/programs-details`,
      transformResponse: (response: { data: ProgramsDetailsResponse }) => response.data
    }),
    getReport: builder.query<Blob, { patientId: string; reportId: number }>({
      query: ({ patientId, reportId }) => ({
        method: 'GET',
        responseHandler: (response: Response) => {
          return response.blob().then((res) => res);
        },
        url: `/patients/${patientId}/reports/details?reportId=${reportId}`
      }),
      transformResponse: (response: Blob) => response
    }),
    getReports: builder.query<ReportProps[], { id: string }>({
      query: ({ id }) => `/patients/${id}/reports`,
      transformResponse: (response: { data: ReportProps[] }) => response.data
    }),
    getSymptoms: builder.query<SymptomsResponseProps, { patientId: string }>({
      providesTags: (result) =>
        result?.scResults
          ? ['Symptom', ...result?.scResults?.map(({ id }) => ({ id, type: 'Symptom' as const }))]
          : ['Symptom'],
      query: ({ patientId }) => `/patients/${patientId}/symptom-checker-results`,
      transformResponse: (response: { data: SymptomsResponseProps }) => ({
        id: response.data.id,
        scResults: response.data.scResults ?? [],
        symptomCheckerStatus: response.data.symptomCheckerStatus ?? ''
      })
    }),
    // Bulk reassign selected patients to a provider. Returns updated[]/failed[] for partial success.
    // See LMD-27084.
    patientsBulkUpdate: builder.mutation<PatientsBulkUpdateResponse, PatientsBulkUpdateParams>({
      invalidatesTags: ['Patient'],
      query: (body) => ({
        body,
        method: 'PATCH',
        url: '/patients-bulk-update'
      })
    }),
    reactivatePatient: builder.mutation<ReactivatePatientResponseProps, { patientId: string }>({
      invalidatesTags: (_result, _error, arg) => [{ id: arg.patientId, type: 'Patient' }],
      query: ({ patientId }) => ({
        method: 'PATCH',
        url: `/patients/${patientId}/reactivate`
      })
    }),
    updateAddress: builder.mutation({
      invalidatesTags: (_result, _error, arg) => [{ id: arg.id, type: 'Patient' }],
      query: ({ address, id }) => ({
        body: {
          address
        },
        method: 'PATCH',
        url: `/patients/${id}/address`
      })
    }),
    updateBenefits: builder.mutation<{ message: string }, { patientId: string }>({
      invalidatesTags: (_result, _error, arg) => [
        { id: arg.patientId, type: 'Patient' },
        'Benefits'
      ],
      query: ({ patientId }) => ({
        method: 'POST',
        url: `/patients/${patientId}/refresh-benefits`
      })
    }),
    updateEmail: builder.mutation({
      invalidatesTags: (_result, _error, arg) => [{ id: arg.id, type: 'Patient' }],
      query: ({ email, id }) => ({
        body: {
          email
        },
        method: 'PATCH',
        url: `/patients/${id}/email`
      })
    }),
    updateGoalWeight: builder.mutation({
      query: ({ goalWeight, id }) => ({
        body: {
          goalWeight
        },
        method: 'PATCH',
        url: `/patients/${id}/wm/goal-weight`
      })
    }),
    updateNotes: builder.mutation<UpdateNotesResponseProps, UpdateNotesRequestProps>({
      invalidatesTags: (_result, _error, arg) => [{ id: arg.id, type: 'Patient' }],
      query: ({ id, notes }) => ({
        body: { notes },
        method: 'PATCH',
        url: `/patients/${id}/notes`
      }),
      transformResponse: (response: { data: UpdateNotesResponseProps }) => response.data
    }),
    updatePatientElationId: builder.mutation<
      { message: string },
      { elationId: string; patientId: string }
    >({
      invalidatesTags: (_result, _error, arg) => [{ id: arg.patientId, type: 'Patient' }],
      query: ({ elationId, patientId }) => ({
        body: { elationId },
        method: 'PATCH',
        url: `/patients/${patientId}/update-patient-elationId`
      })
    }),
    updatePatientsProvider: builder.mutation<
      { message: string },
      { doctorId: string; patientId: string }
    >({
      invalidatesTags: (_result, _error, arg) => [{ id: arg.patientId, type: 'Patient' }],
      query: ({ doctorId, patientId }) => ({
        body: { doctorId },
        method: 'PATCH',
        url: `/patients/${patientId}/provider-update`
      })
    }),
    updatePatientsSpecialtyProvider: builder.mutation<
      { message: string },
      {
        patientId: string;
        platformSpecialtyCode: string;
        providerId: string;
      }
    >({
      invalidatesTags: (_result, _error, arg) => [{ id: arg.patientId, type: 'Patient' }],
      query: ({ patientId, platformSpecialtyCode, providerId }) => ({
        body: { platformSpecialtyCode, providerId },
        method: 'PATCH',
        url: `/patients/${patientId}/specialty-provider`
      })
    }),
    updateVerifyBodyImage: builder.mutation<
      { message: string },
      { manualStatus: BodyImageManualStatus | null; patientId: string }
    >({
      invalidatesTags: (_result, _error, arg) => [
        { id: arg.patientId, type: 'Patient' },
        { id: 'LIST', type: 'Tags' }
      ],
      query: ({ manualStatus, patientId }) => ({
        body: { manualStatus },
        method: 'PATCH',
        url: `/patients/${patientId}/body-image-status`
      })
    })
  })
});

export const {
  useCancelProgramMutation,
  useChangePatientStatusMutation,
  useCreatePlanRecommendMutation,
  useDeactivatePatientMutation,
  useDeleteClearAtRiskStatusMutation,
  useDisablePlanRecommendationMutation,
  useExtendBillingMutation,
  useGetAllergiesQuery,
  useGetBenefitsQuery,
  useGetChartHealthMetricsQuery,
  useGetPatientElationIdQuery,
  useGetPatientIdsQuery,
  useGetPatientProgramsQuery,
  useGetPatientQuery,
  useGetPatientsQuery,
  useGetProblemsQuery,
  useGetProgramsDetailsQuery,
  useGetSymptomsQuery,
  useLazyGetBenefitsQuery,
  useLazyGetBillingHistoryQuery,
  useLazyGetCallTokenQuery,
  useLazyGetHistoryQuery,
  useLazyGetPatientChartQuery,
  useLazyGetPatientIdsQuery,
  useLazyGetPatientProgramsQuery,
  useLazyGetPatientQuery,
  useLazyGetReportQuery,
  usePatientsBulkUpdateMutation,
  useReactivatePatientMutation,
  useUpdateAddressMutation,
  useUpdateBenefitsMutation,
  useUpdateEmailMutation,
  useUpdateGoalWeightMutation,
  useUpdateNotesMutation,
  useUpdatePatientElationIdMutation,
  useUpdatePatientsProviderMutation,
  useUpdatePatientsSpecialtyProviderMutation,
  useUpdateVerifyBodyImageMutation
} = patientsApi;
